import { Injectable, inject } from '@angular/core';
import {Actions, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {interval, mergeMap, Observable, takeUntil, withLatestFrom} from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';
import {SharedNotificationListBaseEffects} from "@shared/components/notification-list/store/shared-notification-list-base.effects";
import {SharedNotificationsApiService} from "@shared/components/notification-list/services/shared-notifications-api.service";
import {
  ISharedNotificationListGetNotificationListPayload,
  setGlobalNotificationAllowance
} from "@shared/components/notification-list/store/shared-notification-list.actions";
import {SharedNotificationListActions} from './shared-notification-list.actions-type';
import {SharedNotificationListSelectors} from './shared-notification-list.selector-type';

@Injectable({
  providedIn: 'root',
})
export class SharedNotificationListApiEffects extends SharedNotificationListBaseEffects {
  protected _actions$: Actions;
  protected _store: Store;
  private _notificationsApiService = inject(SharedNotificationsApiService);


  previousCount: number = 0;
  pollingIntervalMS: number = 30000;

  constructor() {
    const _actions$ = inject(Actions);
    const _store = inject(Store);

    super(_store, _actions$);
  
    this._actions$ = _actions$;
    this._store = _store;
  }

  override getNotificationList(action$: Observable<ISharedNotificationListGetNotificationListPayload>): Observable<any> {
    return action$.pipe(
      mergeMap((payload) => this._notificationsApiService.getNotifications(payload.filters, payload.paging).pipe(
        mergeMap((request) => [
          SharedNotificationListActions.getNotificationListSuccess({
            filters: payload.filters,
            paging: request.paging,
            items: request.items
          })
        ]),
        catchError((error) => [
          SharedNotificationListActions.getNotificationListFail({
            error
          }),
        ])
      )));
  }

  override startNotificationPollingInterval(action$: Observable<any>): any {
    return action$.pipe(
      mergeMap(() => {
          return interval(this.pollingIntervalMS).pipe(
            withLatestFrom(this._store.select(SharedNotificationListSelectors.selectNotificationCount)),
            takeUntil(this._actions$.pipe(ofType(SharedNotificationListActions.stopNotificationPollingInterval))),
            mergeMap(([_, previousCount]) => this._notificationsApiService.getNotificationCount().pipe(
              mergeMap((count) => [
                previousCount !== count ? SharedNotificationListActions.getNotificationList({}) : SharedNotificationListActions.notificationPollingSuccess({ count }),
              ]),
              catchError((error: Error) => [
                SharedNotificationListActions.notificationPollingSuccess({ count: 0 }),
                SharedNotificationListActions.stopNotificationPollingInterval()
              ]),
            ))
          )
        }
      ));
  }

  override stopNotificationPollingInterval(action$: Observable<any>): any {
    return action$;
  }

  override markAllAsRead(action$: Observable<any>): Observable<any> {
    return action$.pipe(
      mergeMap(() => this._notificationsApiService.markAllAsRead().pipe(
        mergeMap(() => [SharedNotificationListActions.getNotificationList({})])
      ))
    );
  }

  override getNotificationSettings(action$: Observable<any>): Observable<any> {
    return action$.pipe(
      mergeMap(() => this._notificationsApiService.getNotificationSettings().pipe(
        mergeMap((settings) => [SharedNotificationListActions.getNotificationSettingsSuccess({ settings })]),
        catchError((error) => [
          SharedNotificationListActions.getNotificationSettingsFail({
            error
          }),
        ])
      ))
    );
  }

  override setGlobalNotificationAllowance(action$: Observable<any>): Observable<any> {
    return action$.pipe(
      mergeMap(({ allowance }) => this._notificationsApiService.setGlobalNotificationAllowance(allowance).pipe(
        mergeMap((settings) => [SharedNotificationListActions.setGlobalNotificationAllowanceSuccess({ settings })]),
        catchError((error) => [
          SharedNotificationListActions.setGlobalNotificationAllowanceFail({
            error
          }),
        ])
      ))
    );
  }
  override setNotificationAllowanceForSubject(action$: Observable<any>): Observable<any> {
    return action$.pipe(
      mergeMap(({ subject, allowance }) => this._notificationsApiService.setNotificationAllowanceForSubject(subject, allowance).pipe(
        mergeMap((settings) => [SharedNotificationListActions.setNotificationAllowanceForSubjectSuccess({ settings })]),
        catchError((error) => [
          SharedNotificationListActions.setNotificationAllowanceForSubjectFail({
            error
          }),
        ])
      ))
    );
  }
}
